import { useMemo, useRef, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { SuiteAppBar } from "@athavita/ui-theme";
import { useAppearanceSync } from "@athavita/ui-theme";
import { ShellHeaderContext } from "./ShellHeader";

// The signed-in shell: the shared Athavita top bar (apps launcher, brand,
// Appearance, account menu) over the routed page. In the editor the bar's
// title region is a portal slot for the file name, Drive location, save
// state and Share (SheetPage → ShellHeaderPortal).

export function SheetShell() {
    const auth = useAuth();
    const { pathname } = useLocation();
    const profile = auth.user?.profile;
    const user = {
        name: profile?.name ?? profile?.email ?? "Signed in",
        ...(profile?.email ? { email: profile.email } : {})
    };

    // Keep the suite look in step with the account copy (Drive BFF
    // /api/me/appearance — Sheets' token carries the athavita.drive scope).
    useAppearanceSync(import.meta.env.VITE_DRIVE_BFF_BASE_URL, auth.user?.access_token);

    // The editor portals its file name + save status into `headerSlot`, and
    // registers a guard so the brand link can't silently drop unsaved edits.
    const [headerSlot, setHeaderSlot] = useState<HTMLDivElement | null>(null);
    const leaveGuardRef = useRef<(() => boolean) | null>(null);
    const headerContext = useMemo(() => ({
        slot: headerSlot,
        setLeaveGuard: (guard: (() => boolean) | null) => { leaveGuardRef.current = guard; }
    }), [headerSlot]);

    // Only the editor has a title; on Home the bar keeps its plain layout.
    const inEditor = pathname.startsWith("/edit");

    return (
        <ShellHeaderContext.Provider value={headerContext}>
            <div className="flex h-screen flex-col bg-background text-foreground">
                <a
                    href="#main-content"
                    className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded-md focus:bg-primary focus:px-3 focus:py-1 focus:text-sm focus:text-primary-foreground"
                >
                    Skip to main content
                </a>
                <SuiteAppBar
                    app="sheets"
                    user={user}
                    onSignOut={() => void auth.signoutRedirect()}
                    renderBrand={(brand) => (
                        <Link
                            to="/"
                            title="Sheets home"
                            onClick={(e) => {
                                if (leaveGuardRef.current && !leaveGuardRef.current()) e.preventDefault();
                            }}
                            className="suite-appbar-btn flex items-center rounded px-1.5 py-1"
                        >
                            {brand}
                        </Link>
                    )}
                    title={inEditor ? <div ref={setHeaderSlot} className="flex min-w-0 flex-1 items-center gap-1.5" /> : undefined}
                />
                <main id="main-content" tabIndex={-1} className="flex min-h-0 flex-1 flex-col">
                    <Outlet />
                </main>
            </div>
        </ShellHeaderContext.Provider>
    );
}
